import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { momentLocalizer } from 'react-big-calendar';
import messages from 'react-big-calendar/lib/utils/messages';
import cn from 'classnames';
import cyrillicToTranslit from 'cyrillic-to-translit-js';
import { useQuery, gql } from '@apollo/client';
import Fuse from 'fuse.js';
import ru from 'convert-layout/ru';
import Lottie from 'react-lottie-player';
import _ from 'lodash';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { toast } from 'react-toastify';

import animationData from 'Shared/spiner_blue.json';
import moment from 'moment';
import CustomVacationCalendar from './CustomVacationCalendar';
import CustomCalendarToolbar from './CustomCalendarToolbar';
import CustomMonthView from './CustomMonthView';
import CustomWeekView from './CustomWeekView';
import CustomEvent from './CustomEvent';
import ReactGA from 'Shared/analytics';
import withErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';
import { logger } from 'RootShared/utility/ErrorLogger';
import useDocumentTitle from 'Shared/hooks/useDocumentTitle';

export const queryGetColleaguesVacation = gql`
  query TabVacationTable @api(name: root) {
    colleagues {
      user {
        id
        firstName
        lastName
      }
    }
  }
`;

const localizer = momentLocalizer(moment);

const TestGutter = () => {
  return null;
};

const transformEmployeesData = (colleagues) => {
  return colleagues.length > 0
    ? colleagues.map((colleague) => {
        return {
          employeeId: +colleague.user.id,
          employeeName: `${colleague.user.firstName} ${colleague.user.lastName}`,
          isPinned: false,
          avatar: colleague.avatar,
          vacations: colleague.leaverequestSet
            ? colleague.leaverequestSet.map((vacation) => {
                return {
                  vacationId: +vacation.id,
                  type: vacation.reason.text,
                  start: moment(vacation.dateFrom).toDate(),
                  end: moment(vacation.dateTo).toDate(),
                };
              })
            : null,
        };
      })
    : [];
};

const TabVacationCalendar = () => {
  const isInitialMount = useRef(true);
  const eventsListRef = useRef(null);
  const employeesListRef = useRef(null);
  const isSyncingLeftScroll = useRef(false);
  const isSyncingRightScroll = useRef(false);

  const { pathname } = useLocation();
  const [isListToggled, setListToggled] = useState(true);
  const [searchInputValue, setSearchInputValue] = useState('');

  useDocumentTitle(pathname.includes('/team/') ? 'Team - Vacation' : 'Colleagues - Vacation');

  const { loading, data, error } = useQuery(queryGetColleaguesVacation, { options: { cache: 'cache-only' } });
  const [employeeEvents, setEmployeeEvents] = useState(() =>
    data && data.colleagues ? transformEmployeesData(data.colleagues) : []
  );
  const localStorageEmployees = useMemo(() => JSON.parse(localStorage.getItem('pinnedEmployees')), []);

  const searchOptions = useMemo(
    () => ({
      shouldSort: true,
      threshold: 0.2,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: ['employeeName'],
    }),
    []
  );
  const searchFuse = useMemo(() => new Fuse(employeeEvents, searchOptions), [employeeEvents, searchOptions]);

  const filterEmployees = useCallback((searchFuse, searchInputValue) => {
    const searchEnglishOccurrences = searchFuse.search(ru.toEn(searchInputValue));
    if (/[а-яА-ЯЁё]/.test(searchInputValue)) {
      const searchOccurrences = searchFuse
        .search(cyrillicToTranslit().transform(searchInputValue))
        .concat(searchEnglishOccurrences);
      return searchOccurrences.filter((elem, index) => searchOccurrences.indexOf(elem) === index);
    }
    return searchEnglishOccurrences;
  }, []);

  useEffect(() => {
    if (data && data.colleagues) {
      setEmployeeEvents(transformEmployeesData(data.colleagues));
    }
  }, [data]);

  useEffect(() => {
    if (isInitialMount.current && employeeEvents.length) {
      isInitialMount.current = false;

      if (!localStorageEmployees || !Array.isArray(localStorageEmployees)) {
        localStorage.setItem('pinnedEmployees', JSON.stringify([]));
      } else if (employeeEvents) {
        setEmployeeEvents(
          employeeEvents.map((employee) => {
            return { ...employee, isPinned: !!localStorageEmployees.find((v) => v === employee.employeeId) };
          })
        );
      }
    }
  }, [employeeEvents, localStorageEmployees]);

  const sortEmployees = useCallback((employees) => {
    const sortedEmployees = _.sortBy(employees, 'employeeName');
    return [
      ...sortedEmployees.filter((employee) => employee.isPinned),
      ...sortedEmployees.filter((employee) => !employee.isPinned),
    ];
  }, []);

  const handlePinnedEmployeesChange = (employeeId) => {
    const idx = employeeEvents.findIndex((item) => item.employeeId === employeeId);
    const newArray = [
      { ...employeeEvents[idx], isPinned: !employeeEvents[idx].isPinned },
      ...employeeEvents.slice(0, idx),
      ...employeeEvents.slice(idx + 1),
    ];

    localStorage.setItem(
      'pinnedEmployees',
      JSON.stringify(newArray.filter((employee) => employee.isPinned).map((employee) => employee.employeeId))
    );

    return newArray;
  };

  const onLeftScroll = useCallback((e) => {
    if (!isSyncingLeftScroll.current) {
      isSyncingRightScroll.current = true;
      eventsListRef.current.scrollTop(e.target.scrollTop);
    }
    isSyncingLeftScroll.current = false;
  }, []);

  const onRightScroll = useCallback((e) => {
    if (!isSyncingRightScroll.current) {
      isSyncingLeftScroll.current = true;
      employeesListRef.current.scrollTop(e.target.scrollTop);
    }
    isSyncingRightScroll.current = false;
  }, []);

  const handleSearchInputChange = useCallback((event) => {
    ReactGA.event({
      category: 'Colleagues Calendar',
      action: 'Search of employee',
    });
    setSearchInputValue(event.target.value);
  }, []);

  const hadleListToggle = useCallback(() => {
    setListToggled(!isListToggled);
  }, [isListToggled]);

  if (loading) {
    return (
      <Lottie
        data-testid="loading_calendar"
        className="lottie"
        loop={true}
        play={true}
        animationData={animationData}
        width={100}
        style={{ width: 100, height: 100, margin: '0px auto' }}
      />
    );
  }

  if (error) {
    logger.error(error);
    toast('Something went wrong on Calendar loading', {
      type: toast.TYPE.ERROR,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }

  if (employeeEvents) {
    return (
      <div
        className={cn('container-calendar', {
          'toggle-list': isListToggled,
        })}
        data-testid="tab_calendar"
      >
        <CustomVacationCalendar
          className="vacation-calendar"
          localizer={localizer}
          defaultDate={new Date()}
          defaultView="week"
          components={{
            toolbar: CustomCalendarToolbar,
            header: () => null,
            event: CustomEvent,
            timeGutterWrapper: TestGutter,
          }}
          popup
          employees={sortEmployees(searchInputValue ? filterEmployees(searchFuse, searchInputValue) : employeeEvents)}
          timeSlots={1440}
          step={1440}
          views={{
            week: CustomWeekView,
            month: CustomMonthView,
          }}
          onListScroll={onLeftScroll}
          style={{ height: '100vh' }}
          messages={messages({
            week: 'Week',
            month: 'Month',
          })}
          onPinnedEmployeesChange={(id) => setEmployeeEvents(handlePinnedEmployeesChange(id))}
          employeesListRef={employeesListRef}
          onEmployeesSearchInputChange={handleSearchInputChange}
          onCalendarScroll={onRightScroll}
          onListClick={hadleListToggle}
          isListToggled={isListToggled}
          eventsListRef={eventsListRef}
          searchInputValue={searchInputValue}
        />
      </div>
    );
  }
};

export default withErrorBoundary(TabVacationCalendar);
